/**
 * Availability, one editor at a time (0216): the edit lease behind the
 * "More" dialog.
 *
 * The lease route's two calls (take or renew; release) live in
 * capacityApi.ts beside the availability PUT.
 *
 * useAvailabilityLease takes the lease when the dialog opens, renews it
 * every AVAILABILITY_LEASE_RENEW_SECONDS while the dialog is open and in
 * use, and releases it on close and on pagehide. After
 * AVAILABILITY_IDLE_RELEASE_MINUTES with no typing or clicks in the
 * dialog it lets go ('paused'); continueEditing takes it again. A lease
 * someone else holds is 'held', and the next renewal takes it when it is
 * free. A lease route that fails is 'unavailable': the dialog stays
 * editable, because the save's version compare is the guard and the lock
 * is only a display.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { releaseAvailabilityLease, takeAvailabilityLease } from '@/lib/capacityApi';

/** The server's lease length (repos/staffAvailabilityLeases.ts). */
export const AVAILABILITY_LEASE_SECONDS = 90;
/** How often the open dialog renews it. */
export const AVAILABILITY_LEASE_RENEW_SECONDS = 30;
/** No typing or clicks for this long and the dialog lets go. */
export const AVAILABILITY_IDLE_RELEASE_MINUTES = 10;

export type AvailabilityLeaseState =
  | { kind: 'pending' }
  | { kind: 'mine' }
  | { kind: 'held'; holderName: string }
  | { kind: 'paused' }
  | { kind: 'unavailable' };

export interface UseAvailabilityLease {
  state: AvailabilityLeaseState;
  /** Call on typing or clicks in the dialog: it resets the idle clock. */
  noteActivity: () => void;
  /** From 'paused': take the lease again, if it is free. */
  continueEditing: () => void;
}

export function useAvailabilityLease(
  slug: string,
  rosterId: string,
  weekStart: string,
  enabled: boolean,
  now: () => number = Date.now,
): UseAvailabilityLease {
  const [state, setState] = useState<AvailabilityLeaseState>({ kind: 'pending' });
  const stateRef = useRef<AvailabilityLeaseState>(state);
  const lastActivity = useRef<number>(now());
  // Bumped by a pause and by Continue editing: a renewal still in flight
  // from before answers into a generation that has moved on and is
  // ignored, so a paused dialog never flips back to editable on its own.
  const generation = useRef(0);
  const set = (next: AvailabilityLeaseState): void => {
    stateRef.current = next;
    setState(next);
  };

  const take = useCallback(async (): Promise<void> => {
    const asked = generation.current;
    try {
      const lease = await takeAvailabilityLease(slug, rosterId, weekStart);
      if (asked !== generation.current) return;
      set(lease.mine ? { kind: 'mine' } : { kind: 'held', holderName: lease.holder_name });
    } catch {
      if (asked !== generation.current) return;
      set({ kind: 'unavailable' });
    }
  }, [slug, rosterId, weekStart]);

  useEffect(() => {
    if (!enabled) return undefined;
    lastActivity.current = now();
    void take();
    const release = (): void => {
      if (stateRef.current.kind === 'mine') releaseAvailabilityLease(slug, rosterId, weekStart);
    };
    const timer = setInterval(() => {
      const current = stateRef.current;
      if (current.kind === 'paused' || current.kind === 'unavailable') return;
      if (current.kind === 'mine' && now() - lastActivity.current >= AVAILABILITY_IDLE_RELEASE_MINUTES * 60_000) {
        // Idle: let go, so a dialog left open does not hold the week all
        // afternoon. What was typed stays in the dialog.
        generation.current += 1;
        releaseAvailabilityLease(slug, rosterId, weekStart);
        set({ kind: 'paused' });
        return;
      }
      void take();
    }, AVAILABILITY_LEASE_RENEW_SECONDS * 1000);
    window.addEventListener('pagehide', release);
    return () => {
      clearInterval(timer);
      window.removeEventListener('pagehide', release);
      release();
    };
  }, [enabled, slug, rosterId, weekStart, take]);

  const noteActivity = useCallback((): void => {
    lastActivity.current = now();
  }, [now]);

  const continueEditing = useCallback((): void => {
    lastActivity.current = now();
    generation.current += 1;
    void take();
  }, [now, take]);

  return { state, noteActivity, continueEditing };
}
